/**
 * Zoom de una imagen dentro de su contenedor, para las previsualizaciones (rFiles, foto del lote de
 * gastos). Reúne los cuatro gestos que se esperan de un visor:
 *
 *  - rueda del ratón: amplía o encoge centrando en el cursor (el pellizco del trackpad llega al
 *    navegador como rueda con Ctrl, así que también vale);
 *  - pellizco con dos dedos: amplía o encoge centrando entre los dedos y sigue el movimiento de la
 *    pareja de dedos, con algo de ganancia (PELLIZCO_GANANCIA) para no tener que abrir tanto;
 *  - arrastre con un dedo o con el ratón cuando la imagen está ampliada: se mueve por ella;
 *  - doble toque o doble clic: alterna entre ajustada y x2,5 alrededor del punto tocado.
 *
 * La imagen no se sale del área: el desplazamiento se acota a lo que sobra de imagen a la escala
 * actual, y por debajo de x1 no se encoge. Todo va con pointer events (ratón, dedo y lápiz en
 * cualquier WebView o navegador actual), y donde no los hay (Safari iOS anterior a 13.4, que sigue
 * entre los navegadores soportados en package.json) con touch y mouse events traducidos a los
 * mismos manejadores, igual que `directives/arrastrable.js`. Sobre el contenedor va
 * `touch-action: none` para que el navegador no haga su propio desplazamiento o zoom de página
 * mientras tanto. La app lleva `user-scalable=no` en el viewport, así que sin esto no hay forma de
 * ampliar una foto en la web.
 *
 * Uso: `const zoom = zoomImagen(img); ... zoom.destruir();` El contenedor de los gestos es el padre
 * de la imagen (o `opciones.contenedor`). `zoom.reiniciar()` vuelve a la escala ajustada.
 */

const ESCALA_MAX = 5;
const ESCALA_DOBLE_TOQUE = 2.5;
/** Dos toques separados menos de esto (ms) y a menos de TOQUE_RADIO px son un doble toque. */
const DOBLE_TOQUE_MS = 300;
const TOQUE_RADIO = 25;
/** Sensibilidad de la rueda: cada 100 unidades de deltaY multiplican la escala por ~1,22. */
const RUEDA_FACTOR = 0.002;
/**
 * Ganancia del pellizco: la escala cambia como (distancia / distancia inicial) ^ esto. Con 1 seria
 * el 1:1 de siempre, que en una foto de ticket se hacia lento; con 1,6 separar los dedos al doble
 * amplia ~x3 y hay que mover menos los dedos para leer la letra pequena.
 */
const PELLIZCO_GANANCIA = 1.6;

export default function zoomImagen(img, opciones) {
  opciones = opciones || {};
  const contenedor = opciones.contenedor || img.parentNode;
  if (!img || !contenedor) return { destruir: function() {}, reiniciar: function() {} };

  let escala = 1;
  let tx = 0;
  let ty = 0;

  /** Punteros apoyados ahora mismo: pointerId -> {x, y}. */
  const punteros = new Map();
  /** Datos del gesto en curso (arrastre o pellizco). */
  let gesto = null;
  /** Último toque suelto, para detectar el doble toque. */
  let ultimoToque = null;

  const limitar = (valor, maximo) => Math.min(maximo, Math.max(-maximo, valor));

  /**
   * Cuánto puede desplazarse la imagen en cada eje: lo que sobra de imagen ampliada respecto al
   * área visible, repartido a los dos lados. Si a esa escala cabe entera en el eje, nada.
   */
  const limites = (e) => {
    const sobraX = (img.offsetWidth * e - contenedor.clientWidth) / 2;
    const sobraY = (img.offsetHeight * e - contenedor.clientHeight) / 2;
    return { x: Math.max(0, sobraX), y: Math.max(0, sobraY) };
  };

  const aplicar = (conTransicion) => {
    const l = limites(escala);
    tx = limitar(tx, l.x);
    ty = limitar(ty, l.y);
    img.style.transition = conTransicion ? 'transform 0.15s ease-out' : 'none';
    img.style.transform = escala === 1 && tx === 0 && ty === 0
      ? ''
      : 'translate(' + tx + 'px, ' + ty + 'px) scale(' + escala + ')';
    img.style.cursor = escala > 1 ? (gesto && gesto.tipo === 'arrastre' ? 'grabbing' : 'grab') : 'zoom-in';
  };

  /** Centro de la imagen sin transformar, en coordenadas de la ventana. */
  const centroBase = () => {
    const r = img.getBoundingClientRect();
    return { x: r.left + r.width / 2 - tx, y: r.top + r.height / 2 - ty };
  };

  /**
   * Cambia la escala dejando quieto el punto de la imagen que hay bajo (px, py) en pantalla: el
   * desplazamiento se recalcula para que ese punto siga donde estaba.
   */
  const escalarEn = (nueva, px, py, centro) => {
    nueva = Math.min(ESCALA_MAX, Math.max(1, nueva));
    if (nueva === escala) return;
    // El centro se mide con la transformación que hay pintada: en mitad de un pellizco, que recalcula
    // desde el estado inicial del gesto, se pasa el centro medido al empezar.
    const c = centro || centroBase();
    const dx = px - c.x;
    const dy = py - c.y;
    const razon = nueva / escala;
    tx = dx - (dx - tx) * razon;
    ty = dy - (dy - ty) * razon;
    escala = nueva;
  };

  const reiniciar = (conTransicion) => {
    escala = 1;
    tx = 0;
    ty = 0;
    aplicar(conTransicion);
  };

  // --- rueda ---
  const onWheel = (e) => {
    e.preventDefault();
    // deltaMode 1 = líneas (Firefox con ratón): se pasa a píxeles aproximados.
    const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    escalarEn(escala * Math.exp(-delta * RUEDA_FACTOR), e.clientX, e.clientY);
    aplicar(false);
  };

  // --- punteros ---
  const distancia = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const punto = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const dosPunteros = () => {
    const it = punteros.values();
    return [it.next().value, it.next().value];
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    punteros.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try { contenedor.setPointerCapture(e.pointerId); } catch (err) { /* sin captura, sigue valiendo */ }
    e.preventDefault();

    if (punteros.size === 2) {
      const [a, b] = dosPunteros();
      gesto = {
        tipo: 'pellizco',
        distancia0: distancia(a, b),
        escala0: escala,
        medio0: punto(a, b),
        centro0: centroBase(),
        tx0: tx,
        ty0: ty
      };
      ultimoToque = null;
    } else if (punteros.size === 1) {
      gesto = {
        tipo: 'arrastre',
        x0: e.clientX,
        y0: e.clientY,
        tx0: tx,
        ty0: ty,
        movido: false,
        inicio: Date.now()
      };
    } else {
      // Un tercer dedo no aporta nada: se ignora hasta que queden dos.
      return;
    }
    aplicar(false);
  };

  const onPointerMove = (e) => {
    if (!punteros.has(e.pointerId)) return;
    punteros.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (!gesto) return;

    if (gesto.tipo === 'pellizco' && punteros.size >= 2) {
      const [a, b] = dosPunteros();
      const medio = punto(a, b);
      const d = distancia(a, b);
      if (gesto.distancia0 > 0) {
        // Primero la escala alrededor del punto medio inicial, luego el desplazamiento de la pareja.
        escala = gesto.escala0;
        tx = gesto.tx0;
        ty = gesto.ty0;
        escalarEn(gesto.escala0 * Math.pow(d / gesto.distancia0, PELLIZCO_GANANCIA), gesto.medio0.x, gesto.medio0.y, gesto.centro0);
        tx += medio.x - gesto.medio0.x;
        ty += medio.y - gesto.medio0.y;
      }
      aplicar(false);
    } else if (gesto.tipo === 'arrastre') {
      const dx = e.clientX - gesto.x0;
      const dy = e.clientY - gesto.y0;
      if (!gesto.movido && Math.hypot(dx, dy) > 5) gesto.movido = true;
      if (escala > 1 && gesto.movido) {
        tx = gesto.tx0 + dx;
        ty = gesto.ty0 + dy;
        aplicar(false);
      }
    }
  };

  const onPointerUp = (e) => {
    if (!punteros.has(e.pointerId)) return;
    punteros.delete(e.pointerId);
    try { contenedor.releasePointerCapture(e.pointerId); } catch (err) { /* nada */ }
    // El doble toque anima el salto de escala; el resto de cambios al soltar se pintan en seco.
    let conTransicion = false;

    if (gesto && gesto.tipo === 'arrastre' && !gesto.movido && e.type === 'pointerup') {
      // Toque sin mover: ¿es el segundo de un doble toque?
      const ahora = Date.now();
      if (ultimoToque && ahora - ultimoToque.t < DOBLE_TOQUE_MS
          && Math.hypot(e.clientX - ultimoToque.x, e.clientY - ultimoToque.y) < TOQUE_RADIO) {
        ultimoToque = null;
        conTransicion = true;
        if (escala > 1) {
          escala = 1;
          tx = 0;
          ty = 0;
        } else {
          escalarEn(ESCALA_DOBLE_TOQUE, e.clientX, e.clientY);
        }
      } else {
        ultimoToque = { t: ahora, x: e.clientX, y: e.clientY };
      }
    }

    if (punteros.size === 1) {
      // Se ha soltado un dedo del pellizco: el que queda sigue como arrastre desde donde está.
      const [a] = dosPunteros();
      gesto = { tipo: 'arrastre', x0: a.x, y0: a.y, tx0: tx, ty0: ty, movido: true, inicio: Date.now() };
    } else if (punteros.size === 0) {
      gesto = null;
    }
    aplicar(conTransicion);
  };

  // Al cambiar la imagen (otra src) se vuelve a la vista ajustada.
  const onLoad = () => reiniciar(false);

  const estiloPrevio = {
    touchAction: contenedor.style.touchAction,
    userSelect: contenedor.style.userSelect,
    overflow: contenedor.style.overflow
  };
  contenedor.style.touchAction = 'none';
  contenedor.style.userSelect = 'none';
  contenedor.style.overflow = 'hidden';
  img.draggable = false;

  // --- respaldo sin pointer events (Safari iOS < 13.4): touch y mouse traducidos a punteros ---
  // Cada dedo es un puntero con su `identifier` y el ratón es el puntero 'raton'. El
  // `preventDefault` del toque es el del evento touch original, que es el que frena el scroll.
  const conPointer = typeof PointerEvent !== 'undefined';
  const comoPuntero = (manejador, tipo) => (e) => {
    const toques = e.changedTouches;
    for (let i = 0; i < toques.length; i++) {
      const t = toques[i];
      manejador({ pointerId: t.identifier, clientX: t.clientX, clientY: t.clientY, button: 0, type: tipo,
        preventDefault: function() { e.preventDefault(); } });
    }
  };
  const onTouchStart = comoPuntero(onPointerDown, 'pointerdown');
  const traducirMove = comoPuntero(onPointerMove, 'pointermove');
  const onTouchMove = (e) => {
    // En iOS viejo tampoco hay `touch-action`: el scroll de la página lo frena este preventDefault.
    if (punteros.size > 0) e.preventDefault();
    traducirMove(e);
  };
  const onTouchEnd = comoPuntero(onPointerUp, 'pointerup');
  const onTouchCancel = comoPuntero(onPointerUp, 'pointercancel');
  const ratonComoPuntero = (manejador, tipo) => (e) => {
    manejador({ pointerId: 'raton', clientX: e.clientX, clientY: e.clientY, button: e.button, type: tipo,
      preventDefault: function() { e.preventDefault(); } });
  };
  // Sin captura de puntero, el movimiento y la suelta del raton se escuchan en `document` mientras
  // esta pulsado: si se suelta fuera del contenedor, el arrastre termina igual y no queda un puntero
  // fantasma que mueva la imagen al volver a pasar por encima sin boton.
  const onMouseMove = ratonComoPuntero(onPointerMove, 'pointermove');
  const soltarRaton = ratonComoPuntero(onPointerUp, 'pointerup');
  const onMouseUp = (e) => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    soltarRaton(e);
  };
  const pulsarRaton = ratonComoPuntero(onPointerDown, 'pointerdown');
  const onMouseDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    pulsarRaton(e);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  contenedor.addEventListener('wheel', onWheel, { passive: false });
  if (conPointer) {
    contenedor.addEventListener('pointerdown', onPointerDown);
    contenedor.addEventListener('pointermove', onPointerMove);
    contenedor.addEventListener('pointerup', onPointerUp);
    contenedor.addEventListener('pointercancel', onPointerUp);
  } else {
    contenedor.addEventListener('touchstart', onTouchStart, { passive: false });
    contenedor.addEventListener('touchmove', onTouchMove, { passive: false });
    contenedor.addEventListener('touchend', onTouchEnd);
    contenedor.addEventListener('touchcancel', onTouchCancel);
    contenedor.addEventListener('mousedown', onMouseDown);
  }
  img.addEventListener('load', onLoad);
  aplicar(false);

  return {
    reiniciar: function() { reiniciar(false); },
    destruir: function() {
      contenedor.removeEventListener('wheel', onWheel);
      if (conPointer) {
        contenedor.removeEventListener('pointerdown', onPointerDown);
        contenedor.removeEventListener('pointermove', onPointerMove);
        contenedor.removeEventListener('pointerup', onPointerUp);
        contenedor.removeEventListener('pointercancel', onPointerUp);
      } else {
        contenedor.removeEventListener('touchstart', onTouchStart);
        contenedor.removeEventListener('touchmove', onTouchMove);
        contenedor.removeEventListener('touchend', onTouchEnd);
        contenedor.removeEventListener('touchcancel', onTouchCancel);
        contenedor.removeEventListener('mousedown', onMouseDown);
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      }
      img.removeEventListener('load', onLoad);
      contenedor.style.touchAction = estiloPrevio.touchAction;
      contenedor.style.userSelect = estiloPrevio.userSelect;
      contenedor.style.overflow = estiloPrevio.overflow;
      img.style.transform = '';
      img.style.transition = '';
      img.style.cursor = '';
      punteros.clear();
      gesto = null;
    }
  };
}
